import { useMemo, useState } from "react";
import { normalizeEntityType } from "../../types";
import type { Interaction, LoungeRound, EntityType } from "../../types";
import type { EntityInfo } from "../../hooks/useEntityMap";
import { AnalyticsBar as Bar } from "../common/AnalyticsBar";
import {
  calcTotalWaitTime,
  calcTotalDelayTime,
  interactionTimelineEvents,
  formatDuration,
  entityTypeLabel,
  expressWaitFactor,
  activityZone,
} from "../../utils/tripDisplay";
import { flightTiming, formatLateness, showTiming } from "../../utils/scheduledTimes";

/** Events with dining-round times projected in (read-only) for metric calcs. */
function eventsFor(i: Interaction): ReturnType<typeof interactionTimelineEvents> {
  return interactionTimelineEvents(
    i.events,
    (i.customResponses as Record<string, unknown>)?.rounds as LoungeRound[] | undefined,
  );
}

interface InteractionsAnalyticsProps {
  /** Already filtered upstream (trip itinerary filter) — analytics just reports. */
  interactions: (Interaction & { id: string })[];
  entityDataMap: Record<string, EntityInfo>;
}

// Experiences (shows, tastings, tours) run to a schedule, so their waits aren't a ride queue's and get their
// own numbers below instead of being averaged in with the rides.
const RIDE_TYPES = new Set<EntityType>(["ATTRACTION"]);
const CUSTOM_TYPE = "CUSTOM";

function typeOf(i: Interaction, entityDataMap: Record<string, EntityInfo>): string {
  return i.entityId ? normalizeEntityType(entityDataMap[i.entityId]?.type) ?? CUSTOM_TYPE : CUSTOM_TYPE;
}

export function InteractionsAnalytics({ interactions, entityDataMap }: InteractionsAnalyticsProps) {
  const [chartMode, setChartMode] = useState<"avg" | "sum">("avg");

  const analytics = useMemo(() => {
    const rideInteractions = interactions.filter((i) => {
      if (!i.entityId) return false;
      const info = entityDataMap[i.entityId];
      return info?.type ? RIDE_TYPES.has(info.type) : false;
    });

    let totalPostedWait = 0; // minutes
    let postedWaitCount = 0;
    let totalActualWait = 0; // seconds
    let actualWaitCount = 0;
    let totalDelay = 0; // seconds

    for (const i of rideInteractions) {
      if (i.postedWait != null && i.postedWait > 0) {
        totalPostedWait += i.postedWait;
        postedWaitCount++;
      }
      const tw = calcTotalWaitTime(eventsFor(i));
      if (tw > 0) {
        totalActualWait += tw;
        actualWaitCount++;
      }
      totalDelay += calcTotalDelayTime(eventsFor(i));
    }

    const countByType: Record<string, number> = {};
    for (const i of interactions) {
      const t = typeOf(i, entityDataMap);
      countByType[t] = (countByType[t] || 0) + 1;
    }

    // Wait by access tier — resolved to the option's display name and grouped
    // by name, so the same tier across rides combines.
    const tiers: Record<string, { count: number; totalPosted: number; totalActual: number }> = {};
    for (const i of rideInteractions) {
      if (!i.accessTierId || !i.entityId) continue;
      const tw = calcTotalWaitTime(eventsFor(i));
      if (!(i.postedWait && i.postedWait > 0) || tw <= 0) continue;
      const opt = entityDataMap[i.entityId]?.activityOptions?.find((o) => o.id === i.accessTierId);
      const name = opt?.displayName ?? i.accessTierId;
      const bucket = (tiers[name] ??= { count: 0, totalPosted: 0, totalActual: 0 });
      bucket.count++;
      bucket.totalPosted += i.postedWait!;
      bucket.totalActual += tw / 60; // minutes
    }
    const tierRows = Object.entries(tiers).map(([name, s]) => {
      const posted = chartMode === "avg" ? s.totalPosted / s.count : s.totalPosted;
      const actual = chartMode === "avg" ? s.totalActual / s.count : s.totalActual;
      const factor = expressWaitFactor(name);
      return {
        name,
        count: s.count,
        posted,
        actual,
        // Expected express wait ≈ posted × 0.5 for express-style tiers.
        expected: factor != null ? posted * factor : null,
      };
    });

    // Shows: the line, and how the day went against the showtime.
    const avg = (xs: number[]) => (xs.length > 0 ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
    const showLineWaits: number[] = [];
    const showEarlyBy: number[] = [];
    const showStartedLate: number[] = [];
    let showCount = 0;
    // Flights: boarding and departure against their scheduled times.
    const flightEarlyBy: number[] = [];
    const flightBoardingLate: number[] = [];
    const flightDepartedLate: number[] = [];
    let flightCount = 0;
    for (const i of interactions) {
      const cr = (i.customResponses ?? {}) as Record<string, unknown>;
      const type = typeOf(i, entityDataMap);
      if (type === "EXPERIENCE") {
        showCount++;
        const tw = calcTotalWaitTime(eventsFor(i));
        if (tw > 0) showLineWaits.push(tw);
        const t = showTiming(i.events, cr.scheduled_time, activityZone(i));
        if (t.earlyBy != null) showEarlyBy.push(t.earlyBy);
        if (t.startedLate != null) showStartedLate.push(t.startedLate);
      } else if (cr.scheduled_boarding || (type === "TRANSPORTATION" && cr.scheduled_time)) {
        const t = flightTiming(i.events, cr.scheduled_time, cr.scheduled_boarding, activityZone(i));
        if (t.earlyBy == null && t.boardingLate == null && t.departedLate == null) continue;
        flightCount++;
        if (t.earlyBy != null) flightEarlyBy.push(t.earlyBy);
        if (t.boardingLate != null) flightBoardingLate.push(t.boardingLate);
        if (t.departedLate != null) flightDepartedLate.push(t.departedLate);
      }
    }

    return {
      shows: {
        count: showCount,
        avgLineWait: avg(showLineWaits),
        avgEarlyBy: avg(showEarlyBy),
        avgStartedLate: avg(showStartedLate),
      },
      flights: {
        count: flightCount,
        avgEarlyBy: avg(flightEarlyBy),
        avgBoardingLate: avg(flightBoardingLate),
        avgDepartedLate: avg(flightDepartedLate),
      },
      total: interactions.length,
      rideCount: rideInteractions.length,
      avgPostedWait: postedWaitCount > 0 ? totalPostedWait / postedWaitCount : 0,
      avgActualWait: actualWaitCount > 0 ? totalActualWait / actualWaitCount : 0,
      totalPostedWait, // minutes
      totalActualWait, // seconds
      totalDelay,
      countByType,
      tierRows,
    };
  }, [interactions, entityDataMap, chartMode]);

  if (interactions.length === 0) {
    return <p className="text-muted">No activities match these filters.</p>;
  }

  const maxCount = Math.max(1, ...Object.values(analytics.countByType));
  const maxTier = Math.max(
    1,
    ...analytics.tierRows.flatMap((r) => [r.posted, r.actual, r.expected ?? 0]),
  );
  const hasExpectedTier = analytics.tierRows.some((r) => r.expected != null);

  return (
    <div className="analytics">
      <div className="analytics-grid">
        <div className="analytics-card">
          <span className="analytics-card-value">{analytics.total}</span>
          <span className="analytics-card-label">Activities</span>
        </div>
        <div className="analytics-card">
          <span className="analytics-card-value">{analytics.rideCount}</span>
          <span className="analytics-card-label">Rides</span>
        </div>
        {analytics.avgPostedWait > 0 && (
          <div className="analytics-card">
            <span className="analytics-card-value">{Math.round(analytics.avgPostedWait)}m</span>
            <span className="analytics-card-label">Avg Posted</span>
          </div>
        )}
        {analytics.avgActualWait > 0 && (
          <div className="analytics-card">
            <span className="analytics-card-value">{formatDuration(analytics.avgActualWait)}</span>
            <span className="analytics-card-label">Avg Actual</span>
          </div>
        )}
        {analytics.totalPostedWait > 0 && (
          <div className="analytics-card">
            <span className="analytics-card-value">{formatDuration(analytics.totalPostedWait * 60)}</span>
            <span className="analytics-card-label">Total Posted</span>
          </div>
        )}
        {analytics.totalActualWait > 0 && (
          <div className="analytics-card">
            <span className="analytics-card-value">{formatDuration(analytics.totalActualWait)}</span>
            <span className="analytics-card-label">Total Actual</span>
          </div>
        )}
        {analytics.totalDelay > 0 && (
          <div className="analytics-card analytics-card-delay">
            <span className="analytics-card-value">{formatDuration(analytics.totalDelay)}</span>
            <span className="analytics-card-label">Total Delay</span>
          </div>
        )}
      </div>

      {analytics.shows.count > 0 && (
        <div className="analytics-section">
          <h3>Experiences</h3>
          <div className="analytics-grid">
            <div className="analytics-card">
              <span className="analytics-card-value">{analytics.shows.count}</span>
              <span className="analytics-card-label">Experiences</span>
            </div>
            {analytics.shows.avgLineWait != null && (
              <div className="analytics-card">
                <span className="analytics-card-value">{formatDuration(analytics.shows.avgLineWait)}</span>
                <span className="analytics-card-label">Avg Line Wait</span>
              </div>
            )}
            {analytics.shows.avgEarlyBy != null && (
              <div className="analytics-card">
                <span className="analytics-card-value">{formatLateness(-analytics.shows.avgEarlyBy, formatDuration)}</span>
                <span className="analytics-card-label">Avg Arrival vs Showtime</span>
              </div>
            )}
            {analytics.shows.avgStartedLate != null && (
              <div className="analytics-card">
                <span className="analytics-card-value">{formatLateness(analytics.shows.avgStartedLate, formatDuration)}</span>
                <span className="analytics-card-label">Avg Start vs Showtime</span>
              </div>
            )}
          </div>
        </div>
      )}

      {analytics.flights.count > 0 && (
        <div className="analytics-section">
          <h3>Flights</h3>
          <div className="analytics-grid">
            <div className="analytics-card">
              <span className="analytics-card-value">{analytics.flights.count}</span>
              <span className="analytics-card-label">Flights</span>
            </div>
            {analytics.flights.avgEarlyBy != null && (
              <div className="analytics-card">
                <span className="analytics-card-value">{formatLateness(-analytics.flights.avgEarlyBy, formatDuration)}</span>
                <span className="analytics-card-label">Avg Arrival vs Boarding</span>
              </div>
            )}
            {analytics.flights.avgBoardingLate != null && (
              <div className="analytics-card">
                <span className="analytics-card-value">{formatLateness(analytics.flights.avgBoardingLate, formatDuration)}</span>
                <span className="analytics-card-label">Avg Boarding</span>
              </div>
            )}
            {analytics.flights.avgDepartedLate != null && (
              <div className="analytics-card">
                <span className="analytics-card-value">{formatLateness(analytics.flights.avgDepartedLate, formatDuration)}</span>
                <span className="analytics-card-label">Avg Departure</span>
              </div>
            )}
          </div>
        </div>
      )}

      {Object.keys(analytics.countByType).length > 1 && (
        <div className="analytics-section">
          <h3>By Type</h3>
          <div className="abar-list">
            {Object.entries(analytics.countByType)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => (
                <div key={type} className="abar-row">
                  <span className="abar-name">
                    {type === CUSTOM_TYPE ? "Custom" : entityTypeLabel(type as EntityType)}
                  </span>
                  <Bar value={count} max={maxCount} label={String(count)} tone="count" />
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="analytics-section">
        <div className="analytics-section-head">
          <h3>Wait by Access Tier</h3>
          {analytics.tierRows.length > 0 && (
            <div className="analytics-toggle">
              <button
                type="button"
                className={chartMode === "avg" ? "on" : ""}
                onClick={() => setChartMode("avg")}
              >
                Average
              </button>
              <button
                type="button"
                className={chartMode === "sum" ? "on" : ""}
                onClick={() => setChartMode("sum")}
              >
                Total
              </button>
            </div>
          )}
        </div>
        {analytics.tierRows.length === 0 ? (
          <p className="text-muted">No rides with an access tier and wait time yet.</p>
        ) : (
          <>
            <div className="analytics-legend">
              <span className="analytics-legend-item">
                <span className="abar-swatch abar-posted" /> Posted
              </span>
              {hasExpectedTier && (
                <span className="analytics-legend-item">
                  <span className="abar-swatch abar-expected" /> Expected
                </span>
              )}
              <span className="analytics-legend-item">
                <span className="abar-swatch abar-actual" /> Actual
              </span>
            </div>
            <div className="abar-list">
              {analytics.tierRows.map((r) => (
                <div key={r.name} className="abar-group">
                  <span className="abar-name">
                    {r.name} <span className="abar-name-meta">· {r.count}</span>
                  </span>
                  <Bar value={r.posted} max={maxTier} label={`${r.posted.toFixed(1)}m`} tone="posted" />
                  {r.expected != null && (
                    <Bar value={r.expected} max={maxTier} label={`${r.expected.toFixed(1)}m`} tone="expected" />
                  )}
                  <Bar value={r.actual} max={maxTier} label={`${r.actual.toFixed(1)}m`} tone="actual" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
